import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Field,
  Page,
  PageHeader,
  SearchSelect,
  Select,
  Switch,
  backLinkClasses,
} from '@evogard/ui';
import type { DeskSettings } from '@evogard/contract';
import { formatInstant, fromColumn, parseMoneyInput, toColumn } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import type { DeskTenant } from './deskBoard.js';
import { DESK_KEY, useDeskSummaries, useInvalidateDesk } from './useDesk.js';

interface OperatorRow {
  userId: string;
  name: string;
  activeFlow: boolean;
  receptiveFlow: boolean;
}

/**
 * A configuração da Mesa: quem trabalha na cobrança (o "papel" do legado), a
 * conferência no ERP e a importação — se ela roda sozinha no dia seguinte ao
 * vencimento, e o que deixa de fora: as cooperativas e a faixa de valor do
 * boleto.
 *
 * A conferência boleto a boleto no ERP nasce desligada (decisão de 19/10/2026):
 * a Mesa já se atualiza ao fim de cada passagem da varredura de boletos, sem
 * chamada a mais. Ligar custa ~4 segundos de fila do SGA por card da fila.
 *
 * ## Uma configuração para as bases, e a lista de operadores por base
 *
 * Desde 29/10/2026 a conferência e a importação são UM formulário que grava em
 * todas as bases que a pessoa configura: a mesma equipe cobra CE e GO no mesmo
 * quadro, e o dono pediu "uma configuração pra CE e GO, pra facilitar". O valor
 * continua guardado por base no servidor — a rota é por base, e a regra 3 vale
 * ali como em todo lugar —, e é a tela que escreve igual nas duas.
 *
 * Duas coisas ficam por base, e as duas por motivo de dado: as cooperativas
 * excluídas, porque o código da cooperativa é de cada SGA; e a lista de
 * operadores, com o "Fechar os pagos agora", porque cada um age sobre a fila
 * de uma base.
 */
export function DeskSettingsScreen() {
  const { ready } = useDeskSummaries({});
  const allowed = ready.filter((s) => s.summary.can.configure);
  const [slug, setSlug] = useState('');
  const tenant = slug || allowed[0]?.tenant.slug || '';
  const selected = allowed.find((s) => s.tenant.slug === tenant)?.tenant;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Configurar a Mesa"
        back={
          <Link to="/billing/desk" className={backLinkClasses()}>
            Mesa de cobrança
          </Link>
        }
      />
      {allowed.length === 0 && ready.length > 0 && (
        <Alert tone="informacao">Seu grupo não configura a Mesa em nenhuma das suas bases.</Alert>
      )}
      {allowed.length > 0 && (
        <Settings key={allowed.map((s) => s.tenant.slug).join('-')} tenants={allowed.map((s) => s.tenant)} />
      )}
      {allowed.length > 1 && (
        <div className="w-56">
          <Select
            label="Base dos operadores"
            value={tenant}
            onChange={(e) => setSlug(e.target.value)}
            help="Quem trabalha e o fechamento dos pagos são de cada base."
          >
            {allowed.map((s) => (
              <option key={s.tenant.slug} value={s.tenant.slug}>
                {s.tenant.name}
              </option>
            ))}
          </Select>
        </div>
      )}
      {selected && (
        <>
          <Operators key={`op-${selected.slug}`} tenant={selected.slug} />
          <Reconcile key={`conc-${selected.slug}`} tenant={selected} />
        </>
      )}
    </Page>
  );
}

function Operators({ tenant }: { tenant: string }) {
  const invalidate = useInvalidateDesk();
  const query = useQuery({
    queryKey: [...DESK_KEY, 'operadores', tenant],
    queryFn: async () =>
      ok(await api.billingDesk.operators({ params: { tenant } }), 200, 'Não foi possível carregar os operadores.'),
  });
  const [rows, setRows] = useState<OperatorRow[]>([]);
  useEffect(() => {
    if (query.data) setRows(query.data.operators.map(({ listed: _listed, ...o }) => o));
  }, [query.data]);

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingDesk.saveOperators({
          params: { tenant },
          body: { operators: rows.map(({ name: _name, ...o }) => o) },
        }),
        200,
        'Não foi possível guardar a lista.',
      ),
    onSuccess: () => invalidate(),
  });

  const candidates = (query.data?.candidates ?? []).filter((c) => !rows.some((r) => r.userId === c.id));
  const patch = (userId: string, p: Partial<OperatorRow>) =>
    setRows((rs) => rs.map((r) => (r.userId === userId ? { ...r, ...p } : r)));
  const add = (userId: string) => {
    const c = candidates.find((x) => x.id === userId);
    if (c) setRows((rs) => [...rs, { userId: c.id, name: c.name, activeFlow: true, receptiveFlow: false }]);
  };
  /* A lista só vale depois de "Guardar a lista": a tela avisa enquanto há mudança pendente. */
  const shape = (list: readonly Omit<OperatorRow, 'name'>[]) =>
    JSON.stringify(list.map(({ userId, activeFlow, receptiveFlow }) => [userId, activeFlow, receptiveFlow]));
  const pending = query.data !== undefined && shape(rows) !== shape(query.data.operators);

  return (
    <Card
      title="Quem trabalha na cobrança"
      descricao="Quem está aqui tem coluna no quadro e recebe card na distribuição. A permissão do grupo diz o que a pessoa pode fazer; esta lista diz quem trabalha os cards. Quem sai continua com os cards que já tem."
    >
      <div className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.userId} className="flex flex-wrap items-center gap-4">
            <span className="min-w-48 flex-1 text-sm font-medium">{r.name}</span>
            <Switch
              label={`Cobrança ativa — ${r.name}`}
              checked={r.activeFlow}
              onCheckedChange={(v) => patch(r.userId, { activeFlow: v })}
              onLabel="Ativa"
              offLabel="Sem ativa"
            />
            <Switch
              label={`Receptiva — ${r.name}`}
              checked={r.receptiveFlow}
              onCheckedChange={(v) => patch(r.userId, { receptiveFlow: v })}
              onLabel="Receptiva"
              offLabel="Sem receptiva"
            />
            <Button size="sm" variant="discreto" onClick={() => setRows((rs) => rs.filter((x) => x.userId !== r.userId))}>
              Tirar da cobrança
            </Button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-texto-suave">Ninguém na cobrança ainda.</p>}
        <div className="w-80">
          <SearchSelect
            label="Adicionar à cobrança"
            placeholder="Digite o nome de quem vai trabalhar…"
            searchPlaceholder="Buscar pelo nome…"
            emptyMessage="Ninguém com esse nome entre as contas ativas desta base."
            options={candidates.map((c) => ({ value: c.id, label: c.name }))}
            value=""
            onChange={add}
            loading={query.isLoading}
            help="A busca é nas contas ativas com acesso a esta base."
          />
        </div>
        {pending && (
          <Alert tone="alerta">A lista mudou e ainda não foi guardada. Clique em "Guardar a lista".</Alert>
        )}
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        {save.isSuccess && !pending && <Alert tone="sucesso">Lista guardada.</Alert>}
        <div>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Guardar a lista
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Os campos que o formulário grava IGUAL em todas as bases, e como cada um se lê. */
const SHARED_FIELDS: ReadonlyArray<{ label: string; show: (s: DeskSettings) => string }> = [
  { label: 'Conferência no ERP', show: (s) => (s.erpCheckEnabled ? 'ligada' : 'desligada') },
  { label: 'Intervalo da conferência', show: (s) => `${s.erpCheckIntervalHours} h` },
  { label: 'Importação automática', show: (s) => (s.importAutoEnabled ? 'ligada' : 'desligada') },
  { label: 'Valor mínimo do boleto', show: (s) => `R$ ${moneyText(s.importMinAmount)}` },
  { label: 'Valor máximo do boleto', show: (s) => `R$ ${moneyText(s.importMaxAmount)}` },
];

/**
 * Os campos em que as bases discordam HOJE, com o valor de cada uma.
 *
 * O formulário abre com os valores da primeira base, e "Guardar" iguala as
 * outras a eles. Sem este aviso, quem abrisse a tela veria um valor só,
 * concluiria que as bases estão assim, e guardaria sem saber que mudou a outra.
 */
function divergences(loaded: ReadonlyArray<{ tenant: DeskTenant; settings: DeskSettings }>) {
  return SHARED_FIELDS.flatMap((field) => {
    const values = loaded.map((l) => ({ tenant: l.tenant, value: field.show(l.settings) }));
    return new Set(values.map((v) => v.value)).size > 1 ? [{ label: field.label, values }] : [];
  });
}

/** O desfecho de cada base ao guardar — nunca um "salvo" sobre todas. */
interface SaveOutcome {
  tenant: DeskTenant;
  error: string | null;
}

function Settings({ tenants }: { tenants: DeskTenant[] }) {
  const invalidate = useInvalidateDesk();
  const queries = useQueries({
    queries: tenants.map((t) => ({
      queryKey: [...DESK_KEY, 'configuracao', t.slug],
      queryFn: async () =>
        ok(
          await api.billingDesk.settings({ params: { tenant: t.slug } }),
          200,
          `Não foi possível carregar a configuração da ${t.name}.`,
        ),
    })),
  });
  const loaded = queries.flatMap((q, i) => (q.data ? [{ tenant: tenants[i]!, settings: q.data }] : []));
  const complete = loaded.length === tenants.length;
  const failures = queries.flatMap((q) => (q.error ? [q.error.message] : []));

  const [enabled, setEnabled] = useState(false);
  const [hours, setHours] = useState('2');
  const [cooperatives, setCooperatives] = useState<Record<string, string>>({});
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [autoImport, setAutoImport] = useState(false);
  /*
   * O formulário se preenche UMA vez, quando todas as bases responderam. Reler
   * depois de guardar não escreve por cima do que a pessoa está digitando; e
   * preencher antes de todas chegarem esconderia a divergência da que faltou.
   */
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    const first = loaded[0]?.settings;
    if (filled || !complete || !first) return;
    setEnabled(first.erpCheckEnabled);
    setHours(String(first.erpCheckIntervalHours));
    setMinAmount(moneyText(first.importMinAmount));
    setMaxAmount(moneyText(first.importMaxAmount));
    setAutoImport(first.importAutoEnabled);
    setCooperatives(
      Object.fromEntries(loaded.map((l) => [l.tenant.slug, l.settings.importExcludedCooperatives.join(', ')])),
    );
    setFilled(true);
  }, [filled, complete, loaded]);

  const differing = complete ? divergences(loaded) : [];

  const save = useMutation({
    mutationFn: async (): Promise<SaveOutcome[]> => {
      const min = parseMoneyInput(minAmount);
      const max = parseMoneyInput(maxAmount);
      if (min === null) throw new Error('Valor mínimo ilegível. Escreva como 150,00.');
      if (max === null) throw new Error('Valor máximo ilegível. Escreva como 100.000,00.');
      const shared = {
        erpCheckEnabled: enabled,
        erpCheckIntervalHours: Math.min(24, Math.max(1, Number(hours) || 2)),
        importMinAmount: toColumn(min),
        importMaxAmount: toColumn(max),
        importAutoEnabled: autoImport,
      };
      /*
       * Uma gravação por base, e o desfecho de cada uma. Não há transação entre
       * duas bases: se a GO recusar depois de a CE gravar, a CE fica gravada, e
       * a tela diz exatamente isso em vez de um "salvo" ou um "erro" sobre as
       * duas.
       */
      const settled = await Promise.allSettled(
        tenants.map(async (t) =>
          ok(
            await api.billingDesk.saveSettings({
              params: { tenant: t.slug },
              body: {
                ...shared,
                importExcludedCooperatives: (cooperatives[t.slug] ?? '')
                  .split(/[,\s]+/)
                  .map((c) => c.trim())
                  .filter(Boolean),
              },
            }),
            200,
            `Não foi possível guardar a configuração da ${t.name}.`,
          ),
        ),
      );
      return settled.map((r, i) => ({
        tenant: tenants[i]!,
        error: r.status === 'fulfilled' ? null : r.reason instanceof Error ? r.reason.message : String(r.reason),
      }));
    },
    onSuccess: () => invalidate(),
  });

  const names = (list: readonly { tenant: DeskTenant }[]) => list.map((o) => o.tenant.name).join(' e ');
  const saved = save.data?.filter((o) => o.error === null) ?? [];
  const refused = save.data?.filter((o) => o.error !== null) ?? [];
  const lastRuns = loaded
    .map(
      (l) =>
        `${l.tenant.slug.toUpperCase()} ${l.settings.erpCheckLastRunAt ? formatInstant(l.settings.erpCheckLastRunAt) : 'nunca'}`,
    )
    .join(' · ');

  return (
    <Card
      title="Configuração"
      descricao={
        tenants.length > 1
          ? `Vale para ${names(tenants.map((tenant) => ({ tenant })))}: ao guardar, os mesmos valores vão para todas. Só as cooperativas excluídas são de cada base.`
          : `Vale para ${tenants[0]!.name}.`
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {failures.map((message) => (
          <Alert key={message} tone="erro">
            {message}
          </Alert>
        ))}
        {differing.length > 0 && (
          <Alert tone="alerta">
            As bases estão diferentes hoje em{' '}
            {differing
              .map(
                (d) =>
                  `${d.label.toLowerCase()} (${d.values.map((v) => `${v.tenant.slug.toUpperCase()} ${v.value}`).join(', ')})`,
              )
              .join('; ')}
            . O formulário abriu com os valores da {loaded[0]!.tenant.name}, e guardar iguala as outras a eles.
          </Alert>
        )}
        <div className="flex flex-col gap-2">
          <Switch
            label="Conferência boleto a boleto no ERP"
            checked={enabled}
            onCheckedChange={setEnabled}
            onLabel="Conferência no ERP ligada"
            offLabel="Conferência no ERP desligada"
          />
          <p className="text-xs text-texto-suave">
            Desligada, a Mesa fecha os pagos ao fim de cada passagem da varredura de boletos, sem chamada a mais
            ao SGA. Ligada, confere a fila mãe boleto a boleto no intervalo abaixo — cada card custa cerca de 4 segundos
            de fila do SGA.
            {complete && ` Última conferência: ${lastRuns}.`}
          </p>
        </div>
        <div className="w-48">
          <Field
            label="Intervalo (horas)"
            type="number"
            min={1}
            max={24}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            disabled={!enabled}
          />
        </div>
        <fieldset className="flex flex-col gap-3">
          <legend className="text-sm font-semibold text-texto">Importação de inadimplentes</legend>
          <div className="flex flex-col gap-2">
            <Switch
              label="Importação automática"
              checked={autoImport}
              onCheckedChange={setAutoImport}
              onLabel="Importação automática ligada"
              offLabel="Importação automática desligada"
            />
            <p className="text-xs text-texto-suave">
              Ligada, no dia seguinte a cada vencimento (5, 10, 15, 20, 25 e 30) a Mesa gera a prévia sozinha a
              partir das 7h e põe os cards na fila mãe — desde que os boletos daquele vencimento tenham sido
              atualizados do ERP depois da meia-noite. Se nenhum candidato tiver veículo no cadastro, a prévia fica
              para conferir em vez de entrar vazia. Ligue só depois de trazer os cards do Kanban: antes disso, o
              mesmo associado ficaria com dois cards.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="w-48">
              <Field
                label="Valor mínimo do boleto"
                inputMode="decimal"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
              />
            </div>
            <div className="w-48">
              <Field
                label="Valor máximo do boleto"
                inputMode="decimal"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-texto-suave">
            Só entram na prévia os boletos dentro desta faixa, com as duas pontas. No Kanban ela era fixa, de
            R$ 150,00 a R$ 100.000,00. A mudança vale para a próxima prévia; a que já foi gerada guarda a faixa
            com que foi feita.
          </p>
          {tenants.map((t) => (
            <Field
              key={t.slug}
              label={tenants.length > 1 ? `Cooperativas que a importação deixa de fora — ${t.name}` : 'Cooperativas que a importação deixa de fora'}
              value={cooperatives[t.slug] ?? ''}
              onChange={(e) => setCooperatives((c) => ({ ...c, [t.slug]: e.target.value }))}
              help={
                t.slug === 'ce'
                  ? 'Códigos separados por vírgula. Na CE, os times da GO dentro do SGA da CE.'
                  : 'Códigos separados por vírgula, do SGA desta base.'
              }
            />
          ))}
        </fieldset>
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        {save.data && refused.length === 0 && <Alert tone="sucesso">Configuração guardada em {names(saved)}.</Alert>}
        {refused.length > 0 && (
          <Alert tone="erro">
            {saved.length > 0 ? `Guardada em ${names(saved)}. ` : ''}
            Não guardou em {names(refused)}: {refused.map((o) => o.error).join(' ')}
            {saved.length > 0 ? ' As bases ficaram diferentes até guardar de novo.' : ''}
          </Alert>
        )}
        <div>
          <Button type="submit" loading={save.isPending} disabled={!complete}>
            {tenants.length > 1 ? `Guardar em ${tenants.map((t) => t.slug.toUpperCase()).join(' e ')}` : 'Guardar'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Fechar os pagos agora, numa base — a conciliação da fila com o boleto pago,
 * sem esperar a próxima passagem. Ficou por base em 29/10/2026, quando a
 * configuração passou a valer para todas: o ato é sobre a fila de uma base.
 */
function Reconcile({ tenant }: { tenant: DeskTenant }) {
  const invalidate = useInvalidateDesk();
  const reconcile = useMutation({
    mutationFn: async (includeOwned: boolean) =>
      ok(
        await api.billingDesk.reconcile({ params: { tenant: tenant.slug }, body: { includeOwned } }),
        200,
        'Não foi possível conferir agora.',
      ),
    onSuccess: () => invalidate(),
  });

  return (
    <Card
      title={`Fechar os pagos agora — ${tenant.name}`}
      descricao="Fecha os cards com boleto pago sem esperar a próxima passagem da varredura."
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button variant="contorno" loading={reconcile.isPending} onClick={() => reconcile.mutate(false)}>
            Fechar os pagos da fila mãe agora
          </Button>
          <Button variant="discreto" loading={reconcile.isPending} onClick={() => reconcile.mutate(true)}>
            Incluir as colunas dos operadores
          </Button>
        </div>
        {reconcile.data && (
          <Alert tone="informacao">
            {reconcile.data.closed} card(s) fechados como pago automático; {reconcile.data.flagged} com dono e boleto
            pago ficaram para o operador.
          </Alert>
        )}
        {reconcile.error && <Alert tone="erro">{reconcile.error.message}</Alert>}
      </div>
    </Card>
  );
}

/** O valor da coluna (`150.00`) do jeito que a pessoa digita (`150,00`). */
function moneyText(column: string): string {
  const value = fromColumn(column);
  if (value === null) return '';
  return (value / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
